import { australianState, countriesList } from "@fla/common";
import {
  AutocompleteGooglePlaces,
  ConsentOrdersPartnerDetailsSchema,
  DatePicker,
  InitialDetails,
  PartnerIcon,
  RadioSelect,
  Select,
  TextInput,
  useAddress
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import "definitions/Yup/yup-extended";
import { useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: InitialDetails;
  onSubmit: (values: InitialDetails) => void;
}

export default function PartnerDetails({ onSubmit, formId, store }: IProps) {
  const { handleSubmit, control, watch, setValue } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(ConsentOrdersPartnerDetailsSchema)
  });

  const { handleNewLocation } = useAddress();

  const onSelectAddress = async (val: any) => {
    if (val?.__isNew__) {
      setValue("spouse.line1_address", val.value);
      return;
    }
    const address = await handleNewLocation(val.label);

    if (address?.locality) {
      setValue("spouse.suburb", address.locality);
    }
    // if (address?.country) {
    //   const country = countriesList.find(v => v.name.toLowerCase() === address.country.toLowerCase());
    //   setValue("spouse.country", country);
    // }
    if (address?.postalCode) {
      setValue("spouse.postcode", address.postalCode);
    }
    if (address?.state) {
      const state = australianState.find(v => v.label?.toLowerCase() === address.state?.toLowerCase())?.value;
      if (state) setValue("spouse.state", state);
    }
    if (address?.streetAddress) {
      setValue("spouse.line1_address", address.streetAddress);
    }
  };
  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b">
        <PartnerIcon classes="w-14 h-14 mx-auto mb-5" />
        <h3 className="text-center">Former Partner's Details</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput
          title="Their First Name"
          control={control}
          name="spouse.first_name"
          id="spouse.first_name"
          placeholder="First Name"
          wrapClasses="col-span-1"
        />
        <TextInput
          title="Their Last Name"
          control={control}
          name="spouse.last_name"
          id="spouse.last_name"
          placeholder="Last Name"
          wrapClasses="col-span-1"
        />
      </div>

      <RadioSelect
        title="Their Gender"
        control={control}
        name="spouse.gender"
        id="spouse.gender"
        options={["male", "female"]}
      />
      <TextInput
        title="Their Occupation"
        control={control}
        name="spouse.job"
        id="spouse.job"
        placeholder=""
        wrapClasses="col-span-1"
      />

      <AutocompleteGooglePlaces
        title={
          <div className="space-y-3">
            <p className="font-bold">Their Residential or Mailing Address</p>
            <p className="text-gray-500 font-semibold uppercase text-xs pb-4">Street Address</p>
          </div>
        }
        name="spouse.line1_address"
        control={control}
        onSelectAddress={onSelectAddress}
      />
      <TextInput
        title="Suburb"
        titleClasses="text-gray-500 font-semibold uppercase text-xs"
        control={control}
        name="spouse.suburb"
        id="spouse.suburb"
        placeholder=""
        wrapClasses="col-span-1"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          title="State"
          titleClasses="text-gray-500 font-semibold uppercase text-xs"
          control={control}
          name="spouse.state"
          id="spouse.state"
          placeholder="Select State"
          options={australianState}
        />
        <TextInput
          title="Postcode"
          titleClasses="text-gray-500 font-semibold uppercase text-xs"
          control={control}
          name="spouse.postcode"
          id="spouse.postcode"
          placeholder=""
          wrapClasses="col-span-1"
        />
      </div>
      {watch("spouse.state") === "overseas" ? (
        <>
          <TextInput
            title="Overseas State"
            titleClasses="font-light text-xs"
            control={control}
            name="spouse.overseas_state"
            id="spouse.overseas_state"
            placeholder="eg: state"
            wrapClasses="col-span-1"
          />

          <Select
            title="Overseas Country"
            titleClasses="font-light text-xs"
            control={control}
            name="spouse.overseas_country"
            id="spouse.overseas_country"
            placeholder="Select Country"
            options={countriesList.map(country => ({
              label: country.name,
              value: country.name
            }))}
          />
        </>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DatePicker title="Their Date Of Birth" control={control} name="spouse.dob" version="v2" />

        <Select
          title="Their Place Of Birth"
          control={control}
          name="spouse.birth_country"
          id="spouse.birth_country"
          placeholder="Select Country"
          options={countriesList.map(country => ({
            label: country.name,
            value: country.name
          }))}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput
          title="Their Mobile Number"
          control={control}
          name="spouse.mobile"
          id="spouse.mobile"
          placeholder="Mobile number"
          wrapClasses="col-span-1"
        />
        <TextInput
          type="email"
          title="Their Email Address"
          control={control}
          name="spouse.email"
          id="spouse.email"
          placeholder="eg: john@doe.ex"
          wrapClasses="col-span-1"
        />
      </div>
    </form>
  );
}
